import { quoteIfNeeded } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { Operator, ParsedToken } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { ContextAttributeFilter, EventPropertyFilter, GroupFilter, MetricFilter } from '@gonfalon/metrics/types';

import {
  CONTEXT_ATTRS_PREFIX,
  CONTEXT_KIND_FIELD,
  DATA_JSON_PREFIX,
} from '../../internal/CreateMetricModal/MetricEventQueryBuilder/metricEventFilterFields';

type FilterParsedToken = Extract<ParsedToken, { type: 'FILTER' }>;

function mapBarOp(operator: Operator): { op: string; negate: boolean } | null {
  switch (operator) {
    case '=':
      return { op: 'in', negate: false };
    case '!=':
      return { op: 'in', negate: true };
    case '//':
      return { op: 'matches', negate: false };
    case '!//':
      return { op: 'matches', negate: true };
    case '**':
      return { op: 'contains', negate: false };
    case '^=':
      return { op: 'startsWith', negate: false };
    case '=^':
      return { op: 'endsWith', negate: false };
    case '>':
      return { op: 'greaterThan', negate: false };
    case '<':
      return { op: 'lessThan', negate: false };
    case '>=':
      return { op: 'greaterThanOrEqual', negate: false };
    case '<=':
      return { op: 'lessThanOrEqual', negate: false };
    default:
      return null;
  }
}

// Backend single-value, non-negatable ops — violating these 400s on save.
const SINGLE_VALUE_OPS = new Set(['greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual']);

function filterTokenToLeaf(token: FilterParsedToken): ContextAttributeFilter | EventPropertyFilter | null {
  if (token.field === CONTEXT_KIND_FIELD) {
    return null;
  }

  const mapped = mapBarOp(token.operator);
  if (!mapped) {
    return null;
  }

  // Field `!` prefix and negating op cancel via XOR.
  const negate = mapped.negate !== Boolean(token.negated);
  const values: Array<string | number | boolean> = Array.isArray(token.value) ? token.value : [token.value];

  if (SINGLE_VALUE_OPS.has(mapped.op) && (values.length !== 1 || negate)) {
    return null;
  }

  const field = token.field;
  let contextRest: string | null = null;
  if (field.startsWith(CONTEXT_ATTRS_PREFIX)) {
    contextRest = field.slice(CONTEXT_ATTRS_PREFIX.length);
  } else if (field.startsWith('ctx.')) {
    contextRest = field.slice('ctx.'.length);
  }
  if (contextRest !== null) {
    const parts = contextRest.split('.');
    const contextKind = parts[0] ?? '';
    const attribute = parts.slice(1).join('.');
    if (!contextKind || !attribute) {
      return null;
    }
    return {
      type: 'contextAttribute',
      op: mapped.op,
      contextKind,
      attribute,
      values,
      negate,
    };
  }

  let attribute = field;
  if (field.startsWith(DATA_JSON_PREFIX)) {
    attribute = field.slice(DATA_JSON_PREFIX.length);
  } else if (field.startsWith('evt.')) {
    attribute = field.slice('evt.'.length);
  }
  return {
    type: 'eventProperty',
    op: mapped.op,
    attribute,
    values,
    negate,
  };
}

// AND binds tighter than OR: split at top-level OR, AND within segments, OR the segments.
// Standalone LOGIC_NOT toggles negateNext (same as buildQueryPayload / Storybook mapper)
// so pasted `NOT field=value` becomes a negated leaf rather than a positive match.
function tokensToFilter(tokens: ParsedToken[]): MetricFilter | undefined {
  const orSegments: MetricFilter[][] = [[]];
  let negateNext = false;
  for (const token of tokens) {
    if (token.type === 'FILTER') {
      const leaf = filterTokenToLeaf(negateNext ? { ...token, negated: !token.negated } : token);
      negateNext = false;
      if (leaf) {
        orSegments[orSegments.length - 1].push(leaf);
      }
    } else if (token.type === 'EXPRESSION') {
      const group = tokensToFilter(token.inner);
      // Metric GroupFilter is typed non-negatable. Apply NOT to a single leaf result;
      // drop a dangling NOT before a multi-child group (mirrors buildQueryPayload).
      if (group) {
        if (negateNext && group.type !== 'group') {
          orSegments[orSegments.length - 1].push({ ...group, negate: !group.negate });
        } else {
          orSegments[orSegments.length - 1].push(group);
        }
      }
      negateNext = false;
    } else if (token.type === 'LOGIC_BOOL' && token.value === 'OR') {
      orSegments.push([]);
    } else if (token.type === 'LOGIC_NOT') {
      negateNext = !negateNext;
    }
  }

  const andGroups = orSegments
    .map((segment): MetricFilter | undefined => {
      if (segment.length === 0) {
        return undefined;
      }
      if (segment.length === 1) {
        return segment[0];
      }
      return { type: 'group', op: 'and', values: segment, negate: false } satisfies GroupFilter;
    })
    .filter((group): group is MetricFilter => group !== undefined);

  if (andGroups.length === 0) {
    return undefined;
  }
  if (andGroups.length === 1) {
    return andGroups[0];
  }

  return { type: 'group', op: 'or', values: andGroups, negate: false } satisfies GroupFilter;
}

export function queryToEventFilter(tokens: ParsedToken[]): MetricFilter | undefined {
  return tokensToFilter(tokens);
}

const OP_TO_UI: Record<string, { operator: Operator; negatedOperator?: Operator }> = {
  in: { operator: '=', negatedOperator: '!=' },
  matches: { operator: '//', negatedOperator: '!//' },
  contains: { operator: '**' },
  startsWith: { operator: '^=' },
  endsWith: { operator: '=^' },
  greaterThan: { operator: '>' },
  greaterThanOrEqual: { operator: '>=' },
  lessThan: { operator: '<' },
  lessThanOrEqual: { operator: '<=' },
};

function rawFieldName(filter: ContextAttributeFilter | EventPropertyFilter): string {
  return filter.type === 'eventProperty'
    ? `${DATA_JSON_PREFIX}${filter.attribute ?? ''}`
    : `${CONTEXT_ATTRS_PREFIX}${filter.contextKind ?? ''}.${filter.attribute ?? ''}`;
}

function renderLeaf(filter: ContextAttributeFilter | EventPropertyFilter): string | null {
  const mapping = OP_TO_UI[filter.op];
  if (!mapping) {
    return null;
  }

  if (SINGLE_VALUE_OPS.has(filter.op) && ((filter.values ?? []).length !== 1 || filter.negate)) {
    return null;
  }

  const field = rawFieldName(filter);

  let prefix = '';
  let operator = mapping.operator;
  if (filter.negate) {
    if (mapping.negatedOperator) {
      operator = mapping.negatedOperator;
    } else {
      prefix = '!';
    }
  }

  const values = (filter.values ?? []).map((value) => quoteIfNeeded(String(value))).join(',');
  return `${prefix}${quoteIfNeeded(field)}:${operator}:${values}`;
}

function renderNode(filter: MetricFilter): string | null {
  if (filter.type !== 'group') {
    return renderLeaf(filter);
  }

  const parts = filter.values
    .map((child) => {
      const rendered = renderNode(child);
      if (rendered === null) {
        return null;
      }
      return child.type === 'group' ? `(${rendered})` : rendered;
    })
    .filter((part): part is string => part !== null);

  if (parts.length === 0) {
    return null;
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return parts.join(filter.op === 'or' ? ' OR ' : ' AND ');
}

function contextKindsFromFilter(filter: MetricFilter): string[] {
  const kinds: string[] = [];
  const seen = new Set<string>();
  const walk = (node: MetricFilter) => {
    if (node.type === 'group') {
      node.values.forEach(walk);
      return;
    }
    if (node.type === 'contextAttribute' && node.contextKind && !seen.has(node.contextKind)) {
      seen.add(node.contextKind);
      kinds.push(node.contextKind);
    }
  };
  walk(filter);
  return kinds;
}

export function eventFilterToQuery(filter: MetricFilter): string | undefined {
  const rendered = renderNode(filter);
  if (rendered === null) {
    return undefined;
  }

  // Multi-kind filters stay unpinned — dropdown is single-select; conflict UI flags the mismatch.
  const contextKinds = contextKindsFromFilter(filter);
  if (contextKinds.length === 1) {
    return `context:=:${quoteIfNeeded(contextKinds[0])} AND ${rendered}`;
  }

  return rendered;
}

export function isRepresentable(filter?: MetricFilter): boolean {
  if (!filter) {
    return true;
  }
  if (filter.type === 'group') {
    return filter.values.every(isRepresentable);
  }
  if ((filter.type !== 'eventProperty' && filter.type !== 'contextAttribute') || !(filter.op in OP_TO_UI)) {
    return false;
  }
  if (SINGLE_VALUE_OPS.has(filter.op)) {
    return (filter.values ?? []).length === 1 && !filter.negate;
  }
  return true;
}

function leafCategory(leaf: ContextAttributeFilter | EventPropertyFilter): 'context' | 'event' {
  return leaf.type === 'contextAttribute' ? 'context' : 'event';
}

function pruneGroup(op: GroupFilter['op'], children: Array<MetricFilter | undefined>): MetricFilter | undefined {
  const surviving = children.filter((child): child is MetricFilter => child !== undefined);
  if (surviving.length === 0) {
    return undefined;
  }
  if (surviving.length === 1) {
    return surviving[0];
  }
  return { type: 'group', op, values: surviving, negate: false } satisfies GroupFilter;
}

export function splitFilterByCategory(filter?: MetricFilter): {
  contextFilter?: MetricFilter;
  eventFilter?: MetricFilter;
} {
  if (!filter) {
    return { contextFilter: undefined, eventFilter: undefined };
  }

  if (filter.type !== 'group') {
    return leafCategory(filter) === 'context'
      ? { contextFilter: filter, eventFilter: undefined }
      : { contextFilter: undefined, eventFilter: filter };
  }

  const contextChildren: Array<MetricFilter | undefined> = [];
  const eventChildren: Array<MetricFilter | undefined> = [];
  for (const child of filter.values) {
    const { contextFilter, eventFilter } = splitFilterByCategory(child);
    contextChildren.push(contextFilter);
    eventChildren.push(eventFilter);
  }

  return {
    contextFilter: pruneGroup(filter.op, contextChildren),
    eventFilter: pruneGroup(filter.op, eventChildren),
  };
}

export function combineEventFilters(context?: MetricFilter, event?: MetricFilter): MetricFilter | undefined {
  if (!context && !event) {
    return undefined;
  }
  if (!context) {
    return event;
  }
  if (!event) {
    return context;
  }
  return { type: 'group', op: 'and', values: [context, event], negate: false } satisfies GroupFilter;
}

function containedCategories(filter?: MetricFilter): Set<'context' | 'event'> {
  if (!filter) {
    return new Set();
  }
  if (filter.type !== 'group') {
    return new Set([leafCategory(filter)]);
  }
  const categories = new Set<'context' | 'event'>();
  for (const child of filter.values) {
    for (const category of containedCategories(child)) {
      categories.add(category);
    }
  }
  return categories;
}

// Reject OR groups that mix categories — split re-ANDs halves and would change meaning.
export function isSplittableAcrossBars(filter?: MetricFilter): boolean {
  if (!filter || filter.type !== 'group') {
    return true;
  }
  if (filter.op === 'or' && containedCategories(filter).size > 1) {
    return false;
  }
  return filter.values.every(isSplittableAcrossBars);
}

export function canEditWithQueryBuilder(filter: MetricFilter | undefined): boolean {
  return isRepresentable(filter) && isSplittableAcrossBars(filter);
}

export function selectedContextKindsFromTokens(tokens: ParsedToken[]): string[] {
  const kinds: string[] = [];
  const seen = new Set<string>();
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER' && token.field === CONTEXT_KIND_FIELD) {
        const values = Array.isArray(token.value) ? token.value : [token.value];
        for (const value of values) {
          const kind = String(value);
          if (kind && !seen.has(kind)) {
            seen.add(kind);
            kinds.push(kind);
          }
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return kinds;
}

function contextAttributeKindsFromTokens(tokens: ParsedToken[]): string[] {
  const kinds: string[] = [];
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER') {
        let rest: string | null = null;
        if (token.field.startsWith(CONTEXT_ATTRS_PREFIX)) {
          rest = token.field.slice(CONTEXT_ATTRS_PREFIX.length);
        } else if (token.field.startsWith('ctx.')) {
          rest = token.field.slice('ctx.'.length);
        }
        if (rest === null) {
          continue;
        }
        const kind = rest.split('.')[0];
        if (kind && rest.includes('.')) {
          kinds.push(kind);
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return kinds;
}

// Unpinned: >1 kind among attrs; pinned: any attr kind ≠ pin.
export type ContextKindConflict = 'pinned' | 'multi';

export function getContextKindConflict(tokens: ParsedToken[]): ContextKindConflict | null {
  const attributeKinds = contextAttributeKindsFromTokens(tokens);
  const pinnedKinds = selectedContextKindsFromTokens(tokens);
  if (pinnedKinds.length === 0) {
    return new Set(attributeKinds).size > 1 ? 'multi' : null;
  }
  const pinned = new Set(pinnedKinds);
  return attributeKinds.some((kind) => !pinned.has(kind)) ? 'pinned' : null;
}

export function hasContextKindConflict(tokens: ParsedToken[]): boolean {
  return getContextKindConflict(tokens) !== null;
}
